import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import SignatureCanvas from 'react-signature-canvas';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { sendQuoteEmail } from '@/lib/emailService';


export const QuoteView = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { config } = useBusinessConfig();
  const [quote, setQuote] = useState<{
    id: string;
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    customer_address: string;
    total_amount: number;
    created_at: string;
    valid_until: string;
    approved: boolean;
    approved_at?: string;
    approved_by?: string;
    signature?: string;
    status: string;
    notes?: string;
    quote_items?: {
      service_name: string;
      description: string;
      price: number;
    }[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const sigCanvas = useRef<SignatureCanvas>(null);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const { data, error } = await supabase
          .from('quotes')
          .select(`
            *,
            quote_items (*)
          `)
          .eq('id', quoteId)
          .single();

        if (error) throw error;
        setQuote(data);
      } catch (error) {
        console.error('Error fetching quote:', error);
        toast.error('Quote not found');
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId]);

  const handleApprove = async () => {
    if (!quoteId) return;

    setApproving(true);
    try {
      const signatureDataUrl = sigCanvas.current?.toDataURL() || null;

      const { error } = await supabase
        .from('quotes')
        .update({
          approved: true,
          approved_at: new Date().toISOString(),
          approved_by: 'Customer',
          signature: signatureDataUrl,
          status: 'approved'
        })
        .eq('id', quoteId);

      if (error) throw error;

      toast.success('Quote approved successfully!');
      
      try {
        if (config && quote) {
          await sendQuoteEmail(
            quote.customer_email,
            {
              quoteNumber: quote.id.slice(0, 8).toUpperCase(),
              customerName: quote.customer_name,
              totalAmount: quote.total_amount,
              businessName: config.biz_name || '',
            }
          );
          toast.success('Quote sent to your email!');
        }
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
        toast.warning('Quote approved but email sending failed. Please contact business directly.');
      }
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error approving quote:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to approve quote');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-slate-800">Quote Not Found</h2>
          <p className="text-slate-600 mt-2">The requested quote does not exist or may have expired.</p>
          <Button 
            onClick={() => navigate('/')} 
            className="mt-6 bg-purple-600 hover:bg-purple-700"
          >
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  const subtotal = quote.total_amount || 0;
  const gst = config?.gst_registered ? subtotal * 0.1 : 0;
  const total = subtotal + gst;

  return (
    <div className="min-h-screen bg-slate-50 pb-24 font-sans">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b z-50 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded-xl flex items-center justify-center">
              <span className="text-white text-xs font-black">
                {config?.biz_name?.charAt(0) || 'T'}
              </span>
            </div>
            <div className="text-left">
              <p className="font-bold text-slate-900 text-sm">{config?.biz_name}</p>
              <p className="text-[10px] text-slate-500">Quote #{quote.id?.slice(0, 8)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Business Header */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">
                {config?.biz_name}
              </h1>
              <p className="text-slate-600 mt-1">{config?.contact_email} • {config?.contact_phone}</p>
            </div>
            {config?.logo_url && (
              <img 
                src={config?.logo_url} 
                alt={config?.biz_name} 
                className="w-16 h-16 object-contain"
              />
            )}
          </div>
          <div className="mt-6 pt-6 border-t border-slate-100">
            <h2 className="font-black text-lg text-slate-900 uppercase tracking-tighter">Quote Details</h2>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Quote ID</p>
                <p className="font-medium text-slate-900">{quote.id?.slice(0, 8)}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Date Created</p>
                <p className="font-medium text-slate-900">
                  {new Date(quote.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Valid Until</p>
                <p className="font-medium text-slate-900">
                  {new Date(quote.valid_until).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Status</p>
                <p className={`font-medium ${quote.approved ? 'text-green-600' : 'text-amber-600'}`}>
                  {quote.approved ? 'APPROVED' : 'PENDING'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8">
          <h2 className="font-black text-lg text-slate-900 uppercase tracking-tighter mb-4">Customer Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Name</p>
              <p className="font-medium text-slate-900">{quote.customer_name}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Email</p>
              <p className="font-medium text-slate-900">{quote.customer_email}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Phone</p>
              <p className="font-medium text-slate-900">{quote.customer_phone}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Address</p>
              <p className="font-medium text-slate-900">{quote.customer_address}</p>
            </div>
          </div>
        </div>

        {/* Services */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8">
          <h2 className="font-black text-lg text-slate-900 uppercase tracking-tighter mb-4">Services Included</h2>
          <div className="space-y-4">
            {quote.quote_items?.map((item, index) => (
              <div key={index} className="flex justify-between items-center pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                <div>
                  <p className="font-bold text-slate-900">{item.service_name}</p>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </div>
                <p className="font-black text-slate-900">${item.price?.toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Total */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8">
          <div className="flex justify-between items-end">
            <div>
              <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Total</p>
              <p className="text-4xl font-black italic tracking-tighter">${total.toFixed(2)}</p>
            </div>
            <p className="text-[8px] font-bold text-slate-500 mb-1 uppercase">
              {config?.gst_registered ? 'Inc. GST' : 'Ex. GST'}
            </p>
          </div>
        </div>

        {/* Approval Section */}
        {!quote.approved ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8">
            <h2 className="font-black text-lg text-slate-900 uppercase tracking-tighter mb-4">Approve & Sign</h2>
            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="font-bold text-amber-800 text-sm">Review Required</p>
                <p className="text-amber-700 text-sm mt-1">
                  Please review the quote details above. By signing below, you acknowledge that you agree to the terms and conditions.
                </p>
              </div>
              
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Signature</p>
                <div className="border-2 border-dashed border-slate-300 rounded-xl overflow-hidden">
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor='black'
                    canvasProps={{ 
                      width: 600, 
                      height: 150,
                      className: 'w-full h-32 bg-white'
                    }}
                  />
                </div>
                <Button 
                  onClick={() => sigCanvas.current?.clear()} 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2"
                >
                  Clear Signature
                </Button>
              </div>
              
              <Button
                onClick={handleApprove}
                disabled={approving}
                className="w-full h-14 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex gap-2"
              >
                {approving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Approve & Send
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <h2 className="font-black text-green-800 uppercase tracking-tighter">Quote Approved!</h2>
                <p className="text-green-700 text-sm">
                  Approved by {quote.approved_by} on {quote.approved_at ? new Date(quote.approved_at).toLocaleDateString() : 'Unknown date'}
                </p>
              </div>
            </div>
            {quote.signature && (
              <div className="mt-4">
                <p className="text-[10px] font-bold text-green-700 uppercase tracking-widest mb-2">Customer Signature</p>
                <img 
                  src={quote.signature} 
                  alt="Customer Signature" 
                  className="max-h-28 border border-green-200 rounded-lg p-2 bg-white"
                />
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <footer className="py-8 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-purple-900/10 text-purple-800 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-4">
              <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
              Professional Service
            </div>
            
            <p className="text-slate-600 text-sm max-w-md mx-auto">
              Thank you for choosing {config?.biz_name}. Our team is committed to providing exceptional service tailored to your needs.
            </p>
            
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Contact Us</p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-600">
                <a href={`mailto:${config?.contact_email}`} className="hover:text-purple-600">
                  {config?.contact_email}
                </a>
                <a href={`tel:${config?.contact_phone}`} className="hover:text-purple-600">
                  {config?.contact_phone}
                </a>
                {config?.website_url && (
                  <a href={config?.website_url} target="_blank" rel="noopener noreferrer" className="hover:text-purple-600">
                    {config?.website_url}
                  </a>
                )}
              </div>
            </div>
            
            <div className="mt-6 text-[8px] font-bold text-slate-500 uppercase tracking-widest">
              © {new Date().getFullYear()} {config?.biz_name}. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};